import React from 'react';
import styled from 'styled-components';
import TextComponent from '../TextComponent';
import { ButtonOption, GlobalAlertShowOption } from './interface';
import theme from '@/styles/theme';

interface GlobalAlertState extends GlobalAlertShowOption {
  isVisible: boolean;
  clickOverlayToClose: boolean;
}

type GlobalAlertProps = {
  isVisible?: boolean;
  title?: string;
  body?: string | React.ReactNode;
  onShow?: () => void;
  buttons?: Array<ButtonOption>;
  clickOverlayToClose?: boolean;
};

const defaultState: GlobalAlertState = {
  isVisible: false,
  title: '',
  body: undefined,
  onShow: undefined,
  buttons: undefined,
  clickOverlayToClose: true
};

export default class GlobalAlert extends React.Component<
  GlobalAlertProps,
  GlobalAlertState
> {
  static _ref: GlobalAlert;

  static setRef(ref: GlobalAlert) {
    GlobalAlert._ref = ref;
  }

  static getRef() {
    return GlobalAlert._ref;
  }

  static getVisible() {
    return GlobalAlert._ref.state.isVisible;
  }

  static show(options: GlobalAlertShowOption) {
    GlobalAlert._ref.show(options);
  }

  static hide() {
    GlobalAlert._ref.hide();
  }

  constructor(props: GlobalAlertProps) {
    super(props);
    this.state = defaultState;
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.getVisible = this.getVisible.bind(this);
    this.onClickOverlay = this.onClickOverlay.bind(this);
  }

  show(options: GlobalAlertShowOption) {
    this.setState(() => ({
      isVisible: true,
      clickOverlayToClose: false,
      ...options
    }));
  }

  hide() {
    this.setState(() => ({ ...defaultState }));
  }

  onClickOverlay() {
    this.state.clickOverlayToClose && this.hide();
  }

  getVisible() {
    return this.state.isVisible;
  }

  render() {
    const {
      isVisible,
      title,
      body,
      onShow,
      buttons = [{ text: '확인', onClick: this.hide }]
    } = this.state;

    onShow && onShow();

    return (
      <ModalOverlay
        visible={isVisible}
        ref={ref => ref?.focus()}
        id="keyboard"
        tabIndex={0}
        onClick={this.onClickOverlay}>
        <ModalWrapper>
          <ModalContent icon={false}>
            <TextWrapper>
              <TextComponent
                fontSize={28}
                fontWeight={700}
                style={{ marginBottom: '20px' }}
                color={theme.DF_COLOR.BLACK}>
                {title}
              </TextComponent>
              {body && (
                <TextComponent
                  fontSize={20}
                  fontWeight={400}
                  style={{ margin: '10px 0' }}
                  color={theme.DF_COLOR.BLACK}>
                  {body}
                </TextComponent>
              )}
            </TextWrapper>
          </ModalContent>
          <ModalButtonWrapper>
            {buttons.map((button, index) => (
              <ModalButton
                border={!!index}
                onClick={button.onClick}
                key={index}>
                {button.text}
              </ModalButton>
            ))}
          </ModalButtonWrapper>
        </ModalWrapper>
      </ModalOverlay>
    );
  }
}

const ModalOverlay = styled.div<{ visible: boolean }>`
  box-sizing: border-box;
  display: ${props => (props.visible ? 'flex' : 'none')};
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1999;
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-flow: column;
  background-color: ${theme.DF_COLOR.WHITE};
  justify-content: center;
  align-items: center;
  border-radius: 5px;
`;

const ModalContent = styled.div<{ icon: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  min-width: 28rem;
  padding: 3.5rem 8rem 3rem;
  text-align: center;
  padding-top: ${props => props.icon && '90px'};
`;

const ModalButtonWrapper = styled.div`
  width: 100%;
  border-top: 1px solid #d1d1d1;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const ModalButton = styled.button<{ border: boolean }>`
  width: 100%;
  padding: 1.5rem 1rem;
  color: ${theme.DF_COLOR.BLACK};
  font-size: 24px;
  font-weight: 600;

  ${({ border }) => border && `border-left: 1px solid #d1d1d1`};

  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-flow: column;
`;
